/**
 * Event Repository
 * Data access layer for events - provides error handling and convenience wrappers
 */

import {
  getAllEvents as fetchAllEvents,
  getEventsPaginated,
  getEventById as fetchEventById,
  getEventWithSessions as fetchEventWithSessions,
  getEventWithStatistics as fetchEventWithStatistics,
  createEvent as addEvent,
  updateEvent as modifyEvent,
  deleteEvent as removeEvent,
  generateEventCode as genEventCode,
  transitionEventStatus as changeEventStatus,
  uploadEventFlyer as uploadFlyer,
  uploadEventPaymentQr as uploadPaymentQr,
  deleteEventPaymentQr as removePaymentQr,
  getEventPaymentQrUrl,
  getEventFlyerUrl,
  deleteEventFlyer as removeFlyer,
  isEventCodeUnique as checkEventCodeUnique,
  getEventsByStatus as fetchEventsByStatus,
  getUpcomingEvents as fetchUpcomingEvents,
  getEventsByDateRange as fetchEventsByDateRange,
  type EventFilters,
} from '@/Services/events.service';

import {
  getEventSessions as fetchEventSessions,
  getSessionById as fetchSessionById,
  createEventSession as addSession,
  createEventSessions as addSessions,
  updateEventSession as modifySession,
  deleteEventSession as removeSession,
  reorderEventSessions as reorderSessions,
  duplicateEventSession as duplicateSession,
  getSessionsByDate as fetchSessionsByDate,
  getSessionCount as fetchSessionCount,
} from '@/Services/eventSessions.service';

import {
  type PaginatedResponse,
  type SortOption,
} from '@/Services/supabaseClient';
import { Database } from '@/types/supabase';
import {
  Event,
  EventWithSessions,
  EventWithStatistics,
  EventSession,
  EventStatus,
} from '@/types/event.types';

type EventInsert = Database['public']['Tables']['events']['Insert'];
type EventUpdate = Database['public']['Tables']['events']['Update'];
type EventSessionInsert =
  Database['public']['Tables']['event_sessions']['Insert'];
type EventSessionUpdate =
  Database['public']['Tables']['event_sessions']['Update'];

// =====================================================
// EVENT OPERATIONS
// =====================================================

/**
 * Get all events (without pagination)
 */
const getAllEvents = async (): Promise<Event[]> => {
  try {
    return await fetchAllEvents();
  } catch (error) {
    console.error('Error in getAllEvents:', error);
    return [];
  }
};

/**
 * Get paginated events with filters and sorting
 */
const getEventsPaginatedList = async (
  page: number = 1,
  pageSize: number = 20,
  filters?: EventFilters,
  sort?: SortOption,
): Promise<PaginatedResponse<Event>> => {
  try {
    return await getEventsPaginated(page, pageSize, filters, sort);
  } catch (error) {
    console.error('Error in getEventsPaginatedList:', error);
    return {
      data: [],
      count: 0,
      page,
      pageSize,
      totalPages: 0,
    };
  }
};

/**
 * Get single event by ID
 */
const getEventById = async (id: string): Promise<Event | null> => {
  try {
    return await fetchEventById(id);
  } catch (error) {
    console.error('Error in getEventById:', error);
    return null;
  }
};

/**
 * Get event with its sessions
 */
const getEventWithSessions = async (
  id: string,
): Promise<EventWithSessions | null> => {
  try {
    return await fetchEventWithSessions(id);
  } catch (error) {
    console.error('Error in getEventWithSessions:', error);
    return null;
  }
};

/**
 * Get event with statistics
 */
const getEventWithStatistics = async (
  id: string,
): Promise<EventWithStatistics | null> => {
  try {
    return await fetchEventWithStatistics(id);
  } catch (error) {
    console.error('Error in getEventWithStatistics:', error);
    return null;
  }
};

/**
 * Create a new event
 */
const createEvent = async (event: EventInsert): Promise<Event | null> => {
  try {
    return await addEvent(event);
  } catch (error) {
    console.error('Error in createEvent:', error);
    throw error;
  }
};

/**
 * Update an existing event
 */
const updateEvent = async (
  id: string,
  updates: EventUpdate,
): Promise<Event | null> => {
  try {
    return await modifyEvent(id, updates);
  } catch (error) {
    console.error('Error in updateEvent:', error);
    throw error;
  }
};

/**
 * Delete an event
 */
const deleteEvent = async (id: string): Promise<boolean> => {
  try {
    await removeEvent(id);
    return true;
  } catch (error) {
    console.error('Error in deleteEvent:', error);
    return false;
  }
};

/**
 * Generate next event code
 */
const generateEventCode = async (year?: number): Promise<string> => {
  try {
    return await genEventCode(year);
  } catch (error) {
    console.error('Error in generateEventCode:', error);
    return `EVT-${year || new Date().getFullYear()}-001`;
  }
};

/**
 * Transition event status
 */
const transitionEventStatus = async (
  id: string,
  newStatus: EventStatus,
  userId: string,
): Promise<Event | null> => {
  try {
    return await changeEventStatus(id, newStatus, userId);
  } catch (error) {
    console.error('Error in transitionEventStatus:', error);
    throw error;
  }
};

/**
 * Upload event flyer
 */
const uploadEventFlyer = async (
  eventId: string,
  file: File,
  userId: string,
): Promise<Event | null> => {
  try {
    return await uploadFlyer(eventId, file, userId);
  } catch (error) {
    console.error('Error in uploadEventFlyer:', error);
    throw error;
  }
};

const uploadEventPaymentQr = async (eventId: string, file: File, userId: string): Promise<Event | null> =>
  uploadPaymentQr(eventId, file, userId);

const deleteEventPaymentQr = async (eventId: string, filename: string, userId: string): Promise<Event | null> =>
  removePaymentQr(eventId, filename, userId);

const getPaymentQrUrl = (filename: string | null | undefined): string | null => getEventPaymentQrUrl(filename);

/**
 * Get public URL for event flyer
 */
const getFlyerUrl = (filename: string | null): string | null => {
  return getEventFlyerUrl(filename);
};

/**
 * Delete event flyer
 */
const deleteEventFlyer = async (
  eventId: string,
  filename: string,
  userId: string,
): Promise<Event | null> => {
  try {
    return await removeFlyer(eventId, filename, userId);
  } catch (error) {
    console.error('Error in deleteEventFlyer:', error);
    throw error;
  }
};

/**
 * Check if event code is unique
 */
const isEventCodeUnique = async (
  code: string,
  excludeId?: string,
): Promise<boolean> => {
  try {
    return await checkEventCodeUnique(code, excludeId);
  } catch (error) {
    console.error('Error in isEventCodeUnique:', error);
    return false;
  }
};

/**
 * Get events by status
 */
const getEventsByStatus = async (status: EventStatus): Promise<Event[]> => {
  try {
    return await fetchEventsByStatus(status);
  } catch (error) {
    console.error('Error in getEventsByStatus:', error);
    return [];
  }
};

/**
 * Get upcoming events
 */
const getUpcomingEvents = async (): Promise<Event[]> => {
  try {
    return await fetchUpcomingEvents();
  } catch (error) {
    console.error('Error in getUpcomingEvents:', error);
    return [];
  }
};

/**
 * Get events by date range
 */
const getEventsByDateRange = async (
  startDate: string,
  endDate: string,
): Promise<Event[]> => {
  try {
    return await fetchEventsByDateRange(startDate, endDate);
  } catch (error) {
    console.error('Error in getEventsByDateRange:', error);
    return [];
  }
};

// =====================================================
// EVENT SESSION OPERATIONS
// =====================================================

/**
 * Get all sessions for an event
 */
const getEventSessions = async (eventId: string): Promise<EventSession[]> => {
  try {
    return await fetchEventSessions(eventId);
  } catch (error) {
    console.error('Error in getEventSessions:', error);
    return [];
  }
};

/**
 * Get single session by ID
 */
const getSessionById = async (id: string): Promise<EventSession | null> => {
  try {
    return await fetchSessionById(id);
  } catch (error) {
    console.error('Error in getSessionById:', error);
    return null;
  }
};

/**
 * Create a new event session
 */
const createEventSession = async (
  session: EventSessionInsert,
): Promise<EventSession | null> => {
  try {
    return await addSession(session);
  } catch (error) {
    console.error('Error in createEventSession:', error);
    throw error;
  }
};

/**
 * Create multiple sessions at once
 */
const createEventSessions = async (
  sessions: EventSessionInsert[],
): Promise<EventSession[]> => {
  try {
    return await addSessions(sessions);
  } catch (error) {
    console.error('Error in createEventSessions:', error);
    throw error;
  }
};

/**
 * Update an existing session
 */
const updateEventSession = async (
  id: string,
  updates: EventSessionUpdate,
): Promise<EventSession | null> => {
  try {
    return await modifySession(id, updates);
  } catch (error) {
    console.error('Error in updateEventSession:', error);
    throw error;
  }
};

/**
 * Delete a session
 */
const deleteEventSession = async (id: string): Promise<boolean> => {
  try {
    await removeSession(id);
    return true;
  } catch (error) {
    console.error('Error in deleteEventSession:', error);
    return false;
  }
};

/**
 * Reorder sessions for an event
 */
const reorderEventSessions = async (
  eventId: string,
  sessionOrders: { id: string; display_order: number }[],
): Promise<EventSession[]> => {
  try {
    return await reorderSessions(eventId, sessionOrders);
  } catch (error) {
    console.error('Error in reorderEventSessions:', error);
    return [];
  }
};

/**
 * Duplicate a session
 */
const duplicateEventSession = async (
  sessionId: string,
  userId: string,
): Promise<EventSession | null> => {
  try {
    return await duplicateSession(sessionId, userId);
  } catch (error) {
    console.error('Error in duplicateEventSession:', error);
    throw error;
  }
};

/**
 * Get sessions by date
 */
const getSessionsByDate = async (
  eventId: string,
  date: string,
): Promise<EventSession[]> => {
  try {
    return await fetchSessionsByDate(eventId, date);
  } catch (error) {
    console.error('Error in getSessionsByDate:', error);
    return [];
  }
};

/**
 * Get count of sessions for an event
 */
const getSessionCount = async (eventId: string): Promise<number> => {
  try {
    return await fetchSessionCount(eventId);
  } catch (error) {
    console.error('Error in getSessionCount:', error);
    return 0;
  }
};

// =====================================================
// EXPORTS
// =====================================================

export {
  // Event operations
  getAllEvents,
  getEventsPaginatedList,
  getEventById,
  getEventWithSessions,
  getEventWithStatistics,
  createEvent,
  updateEvent,
  deleteEvent,
  generateEventCode,
  transitionEventStatus,
  uploadEventFlyer,
  getFlyerUrl,
  deleteEventFlyer,
  uploadEventPaymentQr,
  deleteEventPaymentQr,
  getPaymentQrUrl,
  isEventCodeUnique,
  getEventsByStatus,
  getUpcomingEvents,
  getEventsByDateRange,

  // Session operations
  getEventSessions,
  getSessionById,
  createEventSession,
  createEventSessions,
  updateEventSession,
  deleteEventSession,
  reorderEventSessions,
  duplicateEventSession,
  getSessionsByDate,
  getSessionCount,
};

// Re-export types for convenience
export type { EventFilters } from '@/Services/events.service';
export type { PaginatedResponse, SortOption } from '@/Services/supabaseClient';
